/**
 * 🔒 LOCKED — March 30, 2026
 * PlacementResultsPage — Assessment results with content gate.
 * APPROVED BY OWNER. DO NOT MODIFY without explicit approval.
 *
 * Architecture:
 *   - Teaser (no auth): wellDoneCard + strengthsCard → lock card “Register & Unlock Results”
 *   - Full reveal (authed): tier badge + examiner diagnostic (typewriter) + gaps + Enroll CTA
 *   - Auto-saves results to Firestore via useEffect when currentUser + diagnostic both available
 *   - InlineAuthModal handles Google + email sign-in; sets om_session_active so AuthContext keeps session
 *   - placementEval prompt evaluates behavior not terminology; defaults to operator tier
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions, db } from '../firebase-config';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import InlineAuthModal from '../components/auth/InlineAuthModal';

const placementEval = httpsCallable(functions, 'placementEval');

const OBJECTIVE_LABELS = {
  OBJ_SYSTEM: 'Repeatable AI system',
  OBJ_CONTEXT: 'Complete context & briefs',
  OBJ_BOUNDARIES: 'Boundaries & constraints',
  OBJ_ITERATION: 'Deliberate iteration',
  OBJ_DIAGNOSIS: 'Failure diagnosis',
  OBJ_REPEATABLE: 'Handoff-ready workflow',
};

const TIER_DISPLAY = {
  foundations: { label: 'Foundations', color: '#34d399', price: '$39' },
  operator: { label: 'Operator', color: '#00d4ff', price: '$97' },
  certified: { label: 'Certified', color: '#a78bfa', price: '$149' },
};

export default function PlacementResultsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();

  const [phase, setPhase] = useState('evaluating'); // evaluating | results | auth | complete
  const [diagnostic, setDiagnostic] = useState('');
  const [typedDiagnostic, setTypedDiagnostic] = useState('');
  const [typewriterDone, setTypewriterDone] = useState(false);
  const [recommendedTier, setRecommendedTier] = useState(null);
  const [objectivesMet, setObjectivesMet] = useState([]);
  const [objectivesNotMet, setObjectivesNotMet] = useState([]);
  const [objectiveCount, setObjectiveCount] = useState(null);
  const [error, setError] = useState(null);
  const [redoOffered, setRedoOffered] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  const hasEvaluated = useRef(false);
  const resultsSaved = useRef(false);
  const typingRef = useRef(null);
  const audioRef = useRef(null);
  const openaiTTSFn = useRef(null);

  // Admin preview mode via ?preview query param
  const searchParams = new URLSearchParams(location.search);
  const isPreview = searchParams.get('preview') === '1';

  // Try location.state first, fall back to sessionStorage
  const assessmentContext = location.state?.assessmentContext || (() => {
    try {
      const stored = sessionStorage.getItem('om_assessment_context');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  })();

  // Also check for cached results (page refresh after eval already ran)
  useEffect(() => {
    // Preview mode — load mock data immediately
    if (isPreview) {
      setDiagnostic("You clearly understand your domain — when you talked about using AI for client proposals, the depth of context you described tells me you already know what good output looks like. That instinct for providing context is a real strength, and your willingness to iterate rather than accept the first response shows you're thinking directionally. Where things break down is on the systems side — right now you're relying on instinct each time rather than a repeatable structure, and when output fails, you're retrying with different wording rather than diagnosing why it missed. Those aren't knowledge gaps, they're process gaps, and they're very closable. The Foundations track is built to take exactly what you're already doing well and give it the scaffolding to work every time.");
      setRecommendedTier('foundations');
      setObjectivesMet(['OBJ_CONTEXT', 'OBJ_ITERATION']);
      setObjectivesNotMet(['OBJ_SYSTEM', 'OBJ_BOUNDARIES', 'OBJ_DIAGNOSIS', 'OBJ_REPEATABLE']);
      setObjectiveCount('2/6');
      hasEvaluated.current = true;
      setPhase('results');
      return;
    }

    if (!assessmentContext?.placement) {
      // Check if we have cached results from a previous eval
      try {
        const cached = sessionStorage.getItem('om_placement_result');
        if (cached) {
          const data = JSON.parse(cached);
          setDiagnostic(data.diagnostic || '');
          setRecommendedTier(data.recommendedTier || null);
          setObjectivesMet(data.objectivesMet || []);
          setObjectivesNotMet(data.objectivesNotMet || []);
          setObjectiveCount(data.objectiveCount || null);
          hasEvaluated.current = true;
          setPhase('results');
          return;
        }
      } catch { /* ignore */ }
      navigate('/placement-assessment', { replace: true });
    }
  }, [assessmentContext, navigate, isPreview]);

  // TTS setup — initialize function ref and consume pre-warmed diagnostic audio
  useEffect(() => {
    const ttsCtx = assessmentContext?.micro?.tts;
    if (ttsCtx?.provider === 'openai' && ttsCtx?.muted !== true) {
      openaiTTSFn.current = httpsCallable(getFunctions(), 'openaiTTS');
    }

    // Consume pre-warmed diagnostic TTS promise (started during submit gesture in PlacementAssessmentPage)
    const ttsPromise = window.__omResultsTTS;
    window.__omResultsTTS = null;
    if (ttsPromise) {
      ttsPromise.then(result => {
        if (!result?.data?.audio) return;
        const b64 = result.data.audio;
        const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: result.data.mimeType || 'audio/mpeg' });
        const url = URL.createObjectURL(blob);
        const player = new Audio(url);
        audioRef.current = player;
        player.play().catch(() => {});
      }).catch(() => {});
    }

    return () => { audioRef.current?.pause(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Run the evaluation
  const evaluate = useCallback(async () => {
    if (hasEvaluated.current || !assessmentContext?.placement) return;
    hasEvaluated.current = true;

    const placement = assessmentContext.placement;

    // Use pre-kicked evaluation promise if available (started during submit gesture)
    const preKickedPromise = window.__omEvalPromise || null;
    window.__omEvalPromise = null;

    try {
      let data;
      if (preKickedPromise) {
        const result = await preKickedPromise;
        data = result.data || {};
      } else {
        // Fallback: fresh call (page refresh / direct navigation)
        const result = await placementEval({
          answers: placement.questions.map(q => ({ text: q.text, answer: q.answer })),
          focusTopic: placement.focusTopic || '',
        });
        data = result.data || {};
      }

      setDiagnostic(String(data.diagnostic || ''));
      setRecommendedTier(data.recommendedTier || null);
      setObjectivesMet(data.objectivesMet || []);
      setObjectivesNotMet(data.objectivesNotMet || []);
      setObjectiveCount(data.objectiveCount || null);
      setPhase('results');

      // Save to sessionStorage for persistence
      try {
        sessionStorage.setItem('om_placement_result', JSON.stringify({
          diagnostic: data.diagnostic,
          recommendedTier: data.recommendedTier,
          objectivesMet: data.objectivesMet,
          objectivesNotMet: data.objectivesNotMet,
          objectiveCount: data.objectiveCount,
          focusTopic: placement.focusTopic,
        }));
      } catch { /* non-fatal */ }
    } catch (err) {
      setError('Something went wrong evaluating your assessment. Please go back and try again.');
      setPhase('results');
    }
  }, [assessmentContext]);

  useEffect(() => { evaluate(); }, [evaluate]);

  // Typewriter effect for diagnostic
  useEffect(() => {
    if (phase !== 'results' || !diagnostic) return;
    setTypewriterDone(false);
    let i = 0;
    typingRef.current = setInterval(() => {
      i += 2;
      setTypedDiagnostic(diagnostic.slice(0, i));
      if (i >= diagnostic.length) {
        clearInterval(typingRef.current);
        setTypedDiagnostic(diagnostic);
        setTypewriterDone(true);
      }
    }, 12);
    return () => clearInterval(typingRef.current);
  }, [phase, diagnostic]);

  // Auto-save results to Firestore the moment both currentUser and eval results are available
  useEffect(() => {
    if (!currentUser || resultsSaved.current || !diagnostic || !recommendedTier) return;
    resultsSaved.current = true;
    setDoc(doc(db, 'users', currentUser.uid), {
      savedDemo: {
        recommendedTier,
        objectivesMet,
        objectivesNotMet,
        objectiveCount,
        diagnostic,
        completedAt: new Date().toISOString(),
        demoComplete: true,
        exchangeCount: 1,
      },
    }, { merge: true }).catch(() => {});
  }, [currentUser, diagnostic, recommendedTier]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auth modal closed — AuthContext sets currentUser; auto-save effect fires; page re-renders with full results
  function handleAuthSuccess() {
    setShowAuth(false);
  }

  // Enroll button (post-auth) — go to pricing
  function handleContinueAuthenticated() {
    navigate('/pricing', { replace: true });
  }

  const tier = TIER_DISPLAY[recommendedTier] || TIER_DISPLAY.foundations;

  return (
    <div style={s.page}>
      <div style={s.container}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.headerLabel}>PLACEMENT DIAGNOSTIC</div>
          <h1 style={s.headerTitle}>Your Assessment Results</h1>
        </div>

        {/* Evaluating state */}
        {phase === 'evaluating' && (
          <div style={s.evaluatingCard}>
            <div style={s.pulseRing} />
            <p style={s.evaluatingText}>Analyzing your responses...</p>
            <p style={s.evaluatingSub}>The examiner is reviewing all 6 answers holistically.</p>
          </div>
        )}

        {/* Error state */}
        {error && (
          <div style={s.errorCard}>
            <p style={s.errorText}>{error}</p>
            {redoOffered && (
              <button onClick={() => navigate('/placement-assessment', { replace: true })} style={s.retakeBtn}>
                Retake Assessment →
              </button>
            )}
            {!redoOffered && (
              <button onClick={() => navigate('/', { replace: true })} style={s.retakeBtn}>
                Return Home →
              </button>
            )}
          </div>
        )}

        {/* Results — teaser always visible; full diagnostic gated behind registration */}
        {phase === 'results' && !error && (
          <>
            {/* ── ALWAYS VISIBLE ─────────────────────────────────── */}

            {/* Job well done + concept count */}
            <div style={s.wellDoneCard}>
              <div style={s.wellDoneIcon}>&#10003;</div>
              <div>
                <p style={s.wellDoneTitle}>Assessment Complete</p>
                <p style={s.wellDoneText}>
                  {objectivesMet.length >= 5
                    ? `You demonstrated knowledge of ${objectivesMet.length} out of 6 core AI operator concepts. That's an exceptionally strong result.`
                    : objectivesMet.length >= 3
                    ? `You demonstrated knowledge of ${objectivesMet.length} out of 6 core AI operator concepts. Solid work — there's a clear foundation here to build from.`
                    : objectivesMet.length >= 1
                    ? `You demonstrated knowledge of ${objectivesMet.length} out of 6 core AI operator concepts. The gaps we found are specific and closable.`
                    : `You completed the full assessment. The examiner identified the precise gaps to close — that's exactly what this is for.`
                  }
                </p>
              </div>
            </div>

            {/* What you demonstrated */}
            {objectivesMet.length > 0 && (
              <div style={s.strengthsCard}>
                <div style={s.strengthsLabel}>WHAT YOU DEMONSTRATED</div>
                <p style={s.strengthsText}>
                  {objectivesMet.length >= 4
                    ? `You came in strong. Your responses show you already practice ${objectivesMet.map(k => OBJECTIVE_LABELS[k].toLowerCase()).join(', ')}. These aren't small things — most professionals doing this assessment haven't locked in even half of these.`
                    : objectivesMet.length >= 2
                    ? `Your responses show you already understand ${objectivesMet.map(k => OBJECTIVE_LABELS[k].toLowerCase()).join(' and ')}. These aren't small things — most people doing this assessment haven't locked in even one.`
                    : `Your response demonstrates you're already thinking about ${OBJECTIVE_LABELS[objectivesMet[0]].toLowerCase()} the right way. That instinct matters more than any single technique.`
                  }
                </p>
              </div>
            )}

            {/* ── AUTH GATE ──────────────────────────────────────── */}

            {!currentUser ? (
              /* NOT REGISTERED — show lock card */
              <div style={s.gateCard}>
                <div style={s.gateLockIcon}>&#128274;</div>
                <p style={s.gateTitle}>Your full results are ready.</p>
                <p style={s.gateText}>
                  Register to unlock your complete examiner diagnostic, course placement recommendation,
                  and the specific areas to strengthen. Your results will be saved to your profile.
                </p>
                <button onClick={() => setShowAuth(true)} style={s.ctaBtn}>
                  Register &amp; Unlock Results &#8594;
                </button>
                <p style={s.ctaSubtext}>Free account — takes 30 seconds.</p>
              </div>
            ) : (
              /* REGISTERED — full reveal */
              <>
                {/* Results saved confirmation */}
                <div style={s.welcomeBanner}>
                  <span style={s.welcomeCheck}>&#10003;</span>
                  Results saved to <strong style={{ color: '#e0f8ff' }}>{currentUser.displayName || currentUser.email}</strong>'s profile.
                </div>

                {/* Tier recommendation badge */}
                {recommendedTier && (
                  <div style={{ ...s.tierBadge, borderColor: `${tier.color}44` }}>
                    <span style={s.tierBadgeLabel}>RECOMMENDED TRACK</span>
                    <span style={{ ...s.tierBadgeName, color: tier.color }}>{tier.label}</span>
                  </div>
                )}

                {/* AI diagnostic prose */}
                <div style={s.diagnosticCard}>
                  <div style={s.diagnosticLabel}>EXAMINER DIAGNOSTIC</div>
                  <p style={s.diagnosticText}>{typedDiagnostic}</p>
                </div>

                {/* Gaps identified */}
                {objectivesNotMet.length > 0 && !redoOffered && (
                  <div style={s.gapsCard}>
                    <div style={s.gapsLabel}>GAPS WE IDENTIFIED</div>
                    <p style={s.gapsIntro}>
                      We've noted the following gaps in your responses. These aren't failures —
                      they're the specific process elements the {tier.label} track is built to close.
                    </p>
                    <ul style={s.gapsList}>
                      {objectivesNotMet.map(key => (
                        <li key={key} style={s.gapItem}>
                          <span style={s.gapDot}>&#9702;</span>
                          <span>{OBJECTIVE_LABELS[key]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Redo button if offered */}
                {redoOffered && (
                  <button onClick={() => navigate('/placement-assessment', { replace: true })} style={s.retakeBtn}>
                    Retake Assessment →
                  </button>
                )}

                {/* Enrollment CTA */}
                {!redoOffered && (
                  <div style={s.ctaSection}>
                    <p style={s.ctaText}>
                      {objectivesNotMet.length > 0
                        ? `The ${tier.label} track is designed to address exactly these gaps. As you progress through the course, each of these competencies will be challenged, built, and embedded into how you work with AI.`
                        : `Your responses show strong fundamentals across all six competencies. The ${tier.label} track will push your execution to the next level.`
                      }
                    </p>
                    <button onClick={handleContinueAuthenticated} style={s.ctaBtn}>
                      Enroll in {tier.label} &#8594;
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* Auth modal */}
      {showAuth && <InlineAuthModal onSuccess={handleAuthSuccess} />}
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    background:
      'repeating-linear-gradient(-24deg, rgba(152,236,255,0.05) 0, rgba(152,236,255,0.05) 1px, transparent 1px, transparent 84px), radial-gradient(120% 80% at 18% 16%, rgba(56,189,248,0.2), transparent 52%), radial-gradient(120% 90% at 76% 74%, rgba(167,139,250,0.16), transparent 58%), linear-gradient(165deg, #040716 0%, #091130 44%, #080f26 100%)',
    fontFamily: "'Inter', -apple-system, sans-serif",
    color: '#dce8ff',
    display: 'flex',
    justifyContent: 'center',
    padding: '60px 20px 80px',
  },
  container: {
    width: '100%',
    maxWidth: '640px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '8px',
  },
  headerLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: '#4a7090',
    textTransform: 'uppercase',
    marginBottom: '8px',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: 800,
    color: '#f0f8ff',
    margin: 0,
  },

  // Evaluating
  evaluatingCard: {
    textAlign: 'center',
    padding: '60px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  pulseRing: {
    width: '48px',
    height: '48px',
    borderRadius: '50%',
    border: '3px solid rgba(0,212,255,0.4)',
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  evaluatingText: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#b0c8e8',
    margin: 0,
  },
  evaluatingSub: {
    fontSize: '14px',
    color: '#5a7a9a',
    margin: 0,
  },

  // Error
  errorCard: {
    backgroundColor: 'rgba(255,60,60,0.08)',
    border: '1px solid rgba(255,100,100,0.25)',
    borderRadius: '14px',
    padding: '32px 24px',
    textAlign: 'center',
  },
  errorText: {
    fontSize: '15px',
    color: '#e0a0a0',
    lineHeight: 1.7,
    margin: '0 0 20px 0',
  },

  // Well done card
  wellDoneCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '24px',
    borderRadius: '14px',
    background: 'linear-gradient(165deg, rgba(52,211,153,0.1), rgba(7,12,29,0.65))',
    border: '1px solid rgba(52,211,153,0.3)',
  },
  wellDoneIcon: {
    fontSize: '22px',
    color: '#34d399',
    fontWeight: 800,
    flexShrink: 0,
    lineHeight: 1,
    marginTop: '2px',
  },
  wellDoneTitle: {
    fontSize: '13px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#34d399',
    margin: '0 0 6px 0',
  },
  wellDoneText: {
    fontSize: '15px',
    lineHeight: 1.7,
    color: '#b8e8d0',
    margin: 0,
  },

  // Strengths prose
  strengthsCard: {
    background: 'linear-gradient(165deg, rgba(52,211,153,0.06), rgba(7,12,29,0.7))',
    border: '1px solid rgba(52,211,153,0.18)',
    borderRadius: '14px',
    padding: '28px 24px',
  },
  strengthsLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: '#34d399',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },
  strengthsText: {
    fontSize: '15px',
    lineHeight: 1.8,
    color: '#b8e8d0',
    margin: 0,
  },

  // Scorecard (unused now but kept for reference)
  scoreCard: {
    background: 'linear-gradient(165deg, rgba(8,18,44,0.92), rgba(7,12,29,0.75))',
    border: '1px solid rgba(153,179,214,0.24)',
    borderRadius: '16px',
    padding: '32px 28px',
    boxShadow: '0 24px 64px rgba(1,6,20,0.5)',
  },
  scoreHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '12px',
    marginBottom: '24px',
  },
  scoreCount: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
  },
  scoreNumber: {
    fontSize: '48px',
    fontWeight: 900,
    lineHeight: 1,
  },
  scoreOf: {
    fontSize: '20px',
    fontWeight: 500,
    color: '#5a7a9a',
  },
  scoreLabel: {
    fontSize: '14px',
    color: '#7a9fc0',
    fontWeight: 500,
  },
  objectivesGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  nextStepsLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '1.5px',
    color: '#4a7090',
    textTransform: 'uppercase',
    marginTop: '18px',
    marginBottom: '4px',
  },
  objectiveRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 14px',
    borderRadius: '8px',
    borderLeft: '3px solid',
    backgroundColor: 'rgba(255,255,255,0.02)',
  },
  objectiveIcon: {
    fontSize: '16px',
    fontWeight: 700,
    width: '20px',
    textAlign: 'center',
    flexShrink: 0,
  },
  objectiveLabel: {
    fontSize: '14px',
    fontWeight: 500,
  },

  // Tier badge
  tierBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    padding: '14px 20px',
    borderRadius: '12px',
    border: '1px solid',
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  tierBadgeLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    color: '#5a7a9a',
    textTransform: 'uppercase',
  },
  tierBadgeName: {
    fontSize: '20px',
    fontWeight: 800,
  },

  // Diagnostic
  diagnosticCard: {
    background: 'linear-gradient(165deg, rgba(8,18,44,0.85), rgba(7,12,29,0.65))',
    border: '1px solid rgba(153,179,214,0.18)',
    borderRadius: '14px',
    padding: '28px 24px',
  },
  diagnosticLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: '#4a7090',
    textTransform: 'uppercase',
    marginBottom: '14px',
  },
  diagnosticText: {
    fontSize: '15px',
    lineHeight: 1.8,
    color: '#b0c8e8',
    margin: 0,
    whiteSpace: 'pre-wrap',
  },

  // Gaps identified
  gapsCard: {
    background: 'linear-gradient(165deg, rgba(8,18,44,0.8), rgba(7,12,29,0.6))',
    border: '1px solid rgba(153,179,214,0.18)',
    borderRadius: '14px',
    padding: '28px 24px',
  },
  gapsLabel: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '2px',
    color: '#7a9fc0',
    textTransform: 'uppercase',
    marginBottom: '10px',
  },
  gapsIntro: {
    fontSize: '14px',
    color: '#8aabb8',
    lineHeight: 1.7,
    margin: '0 0 14px 0',
  },
  gapsList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  gapItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '14px',
    color: '#b0c8e8',
    lineHeight: 1.5,
  },
  gapDot: {
    color: '#4a7090',
    fontSize: '16px',
    flexShrink: 0,
  },

  // CTA
  ctaSection: {
    textAlign: 'center',
    marginTop: '8px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  ctaText: {
    fontSize: '15px',
    color: '#7a9fc0',
    lineHeight: 1.6,
    margin: 0,
    maxWidth: '460px',
  },
  ctaBtn: {
    padding: '16px 40px',
    backgroundColor: '#00d4ff',
    color: '#001833',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: 800,
    cursor: 'pointer',
    width: '100%',
    maxWidth: '380px',
  },
  ctaSubtext: {
    fontSize: '13px',
    color: '#5a7a9a',
    margin: '4px 0 0 0',
    lineHeight: 1.5,
    maxWidth: '320px',
    textAlign: 'center',
  },
  retakeBtn: {
    padding: '14px 32px',
    backgroundColor: 'transparent',
    color: '#00d4ff',
    border: '1px solid rgba(0,212,255,0.3)',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    margin: '0 auto',
    display: 'block',
  },

  // Auth gate card
  gateCard: {
    background: 'linear-gradient(165deg, rgba(8,18,44,0.92), rgba(7,12,29,0.78))',
    border: '1px solid rgba(0,212,255,0.22)',
    borderRadius: '16px',
    padding: '40px 28px',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
  },
  gateLockIcon: {
    fontSize: '30px',
    lineHeight: 1,
  },
  gateTitle: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#e0f0ff',
    margin: 0,
  },
  gateText: {
    fontSize: '15px',
    color: '#7a9fc0',
    lineHeight: 1.7,
    margin: 0,
    maxWidth: '420px',
  },

  // Results saved banner (post-auth)
  welcomeBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 20px',
    borderRadius: '10px',
    background: 'rgba(52,211,153,0.08)',
    border: '1px solid rgba(52,211,153,0.2)',
    fontSize: '14px',
    color: '#7ae8b8',
  },
  welcomeCheck: {
    color: '#34d399',
    fontWeight: 700,
    fontSize: '16px',
    flexShrink: 0,
  },
};
